import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Key, Shield, ShieldCheck, Power } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Panel } from "@/components/ui-bits";
import { useAuth } from "@/context/AuthContext";

function RoleBadge({ role, label }) {
  const palette =
    role === "admin"
      ? "bg-amber-100 text-amber-900"
      : role === "dispatcher"
      ? "bg-sky-100 text-sky-800"
      : "bg-stone-100 text-stone-700";
  return <span className={`pill-stage ${palette}`}>{label || role}</span>;
}

function PermissionsDialog({ user, registry, onClose, onSaved }) {
  const [selected, setSelected] = useState([]);
  useEffect(() => {
    setSelected(user?.permissions || []);
  }, [user]);

  const toggle = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const save = async () => {
    try {
      await api.patch(`/users/${user.id}`, { permissions: selected });
      toast.success("Permissions updated");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="permissions-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            Permissions · {user?.name}
          </DialogTitle>
          <DialogDescription>
            Tick the tabs this user can see. Unchecked tabs are hidden from the sidebar and direct URL access is blocked.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {registry.map((p) => {
            const checked = selected.includes(p.key);
            return (
              <label
                key={p.key}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                  checked ? "border-amber-300 bg-amber-50" : "border-border hover:bg-stone-50"
                }`}
                data-testid={`perm-${p.key}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(p.key)}
                  className="h-5 w-5 rounded border-stone-300 text-amber-600 focus:ring-amber-400"
                  data-testid={`perm-check-${p.key}`}
                />
                <span className="text-sm font-medium text-stone-700">{p.label}</span>
                <span className="ml-auto text-[10px] font-mono text-stone-400">{p.key}</span>
              </label>
            );
          })}
        </div>
        <DialogFooter>
          <button
            type="button"
            className="text-xs text-stone-500 hover:underline underline-offset-2 mr-auto"
            onClick={() => setSelected(registry.map((p) => p.key))}
            data-testid="perm-select-all"
          >
            Select all
          </button>
          <button
            type="button"
            className="text-xs text-stone-500 hover:underline underline-offset-2"
            onClick={() => setSelected([])}
            data-testid="perm-select-none"
          >
            Clear all
          </button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="h-12 px-6" data-testid="perm-save">
            Save permissions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPinDialog({ user, onClose, onSaved }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  useEffect(() => {
    setPin("");
    setConfirm("");
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    if (pin.length < 4) {
      toast.error("PIN must be at least 4 digits");
      return;
    }
    if (pin !== confirm) {
      toast.error("PINs don't match");
      return;
    }
    try {
      await api.post(`/users/${user.id}/reset-pin`, { pin });
      toast.success(`PIN reset for ${user.name}`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="reset-pin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Reset PIN · {user?.name}</DialogTitle>
          <DialogDescription>
            Enter the new PIN. The user will need to use it on next sign-in. The change is recorded in the audit log.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <Label>New PIN (4+ digits)</Label>
            <Input
              className="h-12 mt-1"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              data-testid="reset-pin-input"
              autoFocus
            />
          </div>
          <div>
            <Label>Confirm</Label>
            <Input
              className="h-12 mt-1"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-pin-confirm"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="h-12 px-6" data-testid="reset-pin-submit">
              Reset PIN
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserRow({
  u,
  registry,
  roleLabel,
  onEditPerms,
  onEditPin,
  onToggleActive,
  onEdit,
  onDelete,
}) {
  const isAdmin = u.role === "admin";
  const permCount = isAdmin ? registry.length : u.permissions?.length || 0;
  const inactive = u.active === false;
  return (
    <tr
      key={u.id}
      className={`border-t border-border h-16 ${inactive ? "opacity-60" : ""}`}
      data-testid={`user-row-${u.id}`}
      data-active={!inactive}
    >
      <td>
        <div className="font-medium">{u.name}</div>
        {u.email ? <div className="text-xs text-stone-500">{u.email}</div> : null}
      </td>
      <td><RoleBadge role={u.role} label={roleLabel(u.role)} /></td>
      <td className="text-sm">
        {isAdmin ? (
          <span className="text-amber-700 font-semibold inline-flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5" /> All tabs (admin)
          </span>
        ) : (
          <button
            type="button"
            className="text-stone-700 hover:underline underline-offset-2 inline-flex items-center gap-1.5"
            onClick={() => onEditPerms(u)}
            data-testid={`edit-perms-${u.id}`}
          >
            <Shield className="h-3.5 w-3.5" />
            {permCount} / {registry.length} tabs
          </button>
        )}
      </td>
      <td>
        <span
          className={`pill-stage ${
            inactive ? "bg-stone-200 text-stone-600" : "bg-emerald-100 text-emerald-800"
          }`}
        >
          {inactive ? "Inactive" : "Active"}
        </span>
      </td>
      <td className="text-stone-600 text-sm">{u.created_at?.slice(0, 10)}</td>
      <td>
        <div className="flex justify-end gap-1 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEditPin(u)}
            data-testid={`reset-pin-${u.id}`}
            title="Set a new PIN for this user"
          >
            <Key className="h-3.5 w-3.5 mr-1" /> PIN
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onToggleActive(u)}
            data-testid={`toggle-active-${u.id}`}
            title={inactive ? "Reactivate user" : "Deactivate user"}
          >
            <Power className={`h-4 w-4 ${inactive ? "text-stone-500" : "text-emerald-700"}`} />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onEdit(u)}
            data-testid={`edit-user-${u.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onDelete(u)}
            data-testid={`delete-user-${u.id}`}
          >
            <Trash2 className="h-4 w-4 text-rose-600" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export default function UsersPanel() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editingPerms, setEditingPerms] = useState(null);
  const [editingPin, setEditingPin] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { name: "", role: "staff", pin: "", email: "" };
  const [form, setForm] = useState(empty);

  const load = async () => setRows((await api.get("/users")).data);
  const loadRegistry = async () => {
    try {
      const { data } = await api.get("/users/permissions");
      setRegistry(data.permissions || []);
    } catch {
      setRegistry([]);
    }
  };
  const loadRoles = async () => {
    try {
      const { data } = await api.get("/roles");
      setRoles(data || []);
    } catch {
      setRoles([]);
    }
  };
  useEffect(() => {
    load();
    loadRegistry();
    loadRoles();
  }, []);

  const adminExists = useMemo(() => rows.some((r) => r.role === "admin"), [rows]);
  const editingUser = editId ? rows.find((r) => r.id === editId) : null;
  const roleOptions = useMemo(
    () =>
      roles.filter((r) => {
        if (r.key !== "admin") return true;
        if (!adminExists) return true;
        return editingUser?.role === "admin";
      }),
    [roles, adminExists, editingUser]
  );

  const roleLabel = (key) => roles.find((r) => r.key === key)?.label || key;

  const startNew = () => {
    setEditId(null);
    const fallback = roles.find((r) => r.key !== "admin")?.key || "staff";
    setForm({ name: "", role: fallback, pin: "", email: "" });
    setOpen(true);
  };
  const startEdit = (u) => {
    setEditId(u.id);
    setForm({ name: u.name, role: u.role, pin: "", email: u.email || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        const body = { name: form.name, role: form.role, email: form.email || null };
        if (form.pin) body.pin = form.pin;
        await api.patch(`/users/${editId}`, body);
      } else {
        if (form.pin.length < 4) {
          toast.error("PIN must be at least 4 digits");
          return;
        }
        await api.post("/users", form);
      }
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (u) => {
    if (u.id === me?.id) {
      toast.error("You can't delete your own account.");
      return;
    }
    if (!confirm(`Delete user "${u.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const toggleActive = async (u) => {
    if (u.id === me?.id) {
      toast.error("You can't deactivate your own account.");
      return;
    }
    try {
      await api.patch(`/users/${u.id}`, { active: !(u.active !== false) });
      toast.success(u.active !== false ? "Deactivated" : "Reactivated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Panel
      title={`Users (${rows.length})`}
      action={
        <Button className="h-11 gap-2" onClick={startNew} data-testid="new-user-button">
          <Plus className="h-4 w-4" /> New user
        </Button>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="users-table">
          <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
            <tr>
              <th className="py-3">Name</th>
              <th>Role</th>
              <th>Permissions</th>
              <th>Status</th>
              <th>Created</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <UserRow
                key={u.id}
                u={u}
                registry={registry}
                roleLabel={roleLabel}
                onEditPerms={setEditingPerms}
                onEditPin={setEditingPin}
                onToggleActive={toggleActive}
                onEdit={startEdit}
                onDelete={remove}
              />
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? "Edit" : "New"} user</DialogTitle>
            <DialogDescription>
              Set the name, role and (for new users) PIN. Use the permissions matrix on the row to fine-tune access.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                className="h-12 mt-1"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="user-name"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-12 mt-1" data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((r) => (
                    <SelectItem key={r.key} value={r.key} data-testid={`user-role-opt-${r.key}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {adminExists && !(editingUser?.role === "admin") && (
                <p className="text-[10px] text-stone-500 mt-1">
                  Admin role is hidden — only one admin account is allowed.
                </p>
              )}
            </div>
            <div>
              <Label>Email (optional)</Label>
              <Input
                className="h-12 mt-1"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="user-email"
                placeholder="staff@oz-bakery.com"
              />
            </div>
            <div>
              <Label>{editId ? "New PIN (leave empty to keep)" : "PIN (4+ digits)"}</Label>
              <Input
                className="h-12 mt-1"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.pin}
                onChange={(e) => setForm({ ...form, pin: e.target.value })}
                data-testid="user-pin"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="h-12 px-6" data-testid="user-submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PermissionsDialog
        user={editingPerms}
        registry={registry}
        onClose={() => setEditingPerms(null)}
        onSaved={() => {
          setEditingPerms(null);
          load();
        }}
      />
      <ResetPinDialog
        user={editingPin}
        onClose={() => setEditingPin(null)}
        onSaved={() => setEditingPin(null)}
      />
    </Panel>
  );
}
